import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material'
import { GreenText } from 'components'
import { downloadCustomerExportReport, getCustomerExportReportForm } from 'controllers/reports'
import { BasicCompanyDto } from 'dtos/companies/BasicCompanyDto'
import { useLoadingState } from 'hooks'
import { useEffect, useState } from 'react'

interface CustomerExportReportDialogProps {
  onClose: () => void
  open: boolean
}

export default function CustomerExportReportDialog({ onClose, open }: CustomerExportReportDialogProps) {
  const [companies, setCompanies] = useState<BasicCompanyDto[]>([])
  const [selectedCompany, setSelectedCompany] = useState<BasicCompanyDto | null>(null)
  const [loadingState, setLoadingState] = useLoadingState({
    isDownloading: false,
    isGettingForm: false
  })

  useEffect(() => {
    if (!open) {
      return
    }

    setLoadingState('isGettingForm', true)

    getCustomerExportReportForm()
      .then(response => {
        setCompanies(response.value.companies)
      })
      .finally(() => {
        setLoadingState('isGettingForm', false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleDownload = () => {
    if (!selectedCompany?.companyGuid || !selectedCompany.name) {
      return
    }

    setLoadingState('isDownloading', true)

    downloadCustomerExportReport(selectedCompany.companyGuid, selectedCompany.name)
      .then(() => {
        onClose()
      })
      .finally(() => {
        setLoadingState('isDownloading', false)
      })
  }

  return (
    <Dialog
      fullWidth
      id='customer-export-report-dialog'
      maxWidth='sm'
      onClose={loadingState.isDownloading ? undefined : onClose}
      open={open}
    >
      <DialogTitle>Customer Export Report</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              autoHighlight
              disabled={loadingState.isDownloading || loadingState.isGettingForm}
              getOptionLabel={company => company.name ?? ''}
              id='customer-export-report-company'
              isOptionEqualToValue={(option, value) => option.companyGuid === value.companyGuid}
              loading={loadingState.isGettingForm}
              noOptionsText={
                loadingState.isGettingForm
                  ? 'Loading Exporters and Buyers...'
                  : 'No Exporters or Buyers found.'
              }
              onChange={(_e, value) => {
                setSelectedCompany(value)
              }}
              options={companies}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Exporter / Buyer'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingState.isGettingForm && (
                          <InputAdornment position='end'>
                            <CircularProgress size={24} />
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              value={selectedCompany}
            />
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`This report downloads a CSV for vehicles where the selected company is either the vehicle's Buyer or Exporter. Rows are sorted by Stock # with blank Stock # rows at the top. Blank Stock # rows are sorted by VIN.

**Export columns and data sources**

**Exporter / Buyer**

Selected company when this report was generated. This column always shows the selected company name.

**VIN**

Add/Edit VIN Dialog, VIN field.

**Model Year**

Add/Edit VIN Dialog, Year field.

**Make**

Add/Edit VIN Dialog, Make field.

**Model**

Add/Edit VIN Dialog, Model field.

**Color**

Not in VTS2. Left blank for all records.

**Stock #**

Vehicle Details > Hero card. The report sorts by this column, with blank Stock # rows first.

**BWI Ownership Received**

Vehicle Details > Documents. Shows TRUE when at least one CAN Ownership document is associated with the vehicle.

**Last Updated Date**

Left blank for all records.

**Last Status**

Left blank for all records.

**Manufacturer**

Add/Edit VIN Dialog, OEM field.

**Entry #**

Inbound Trips/Inbound Trip Details, Entry # of the Inbound Trip the vehicle is associated with.

**Manufacturer Month**

Add/Edit VIN Dialog, MFG Build Date (Month) field.

**Manufacturer Year**

Add/Edit VIN Dialog, MFG Build Date (Year) field.

**Bond Number**

Vehicle Details > Bond tab.

**Amount**

Add/Edit VIN Dialog, Amount (USD) field.

**Exporter**

Add/Edit VIN Dialog, Exporter dropdown.

**PAPS #**

Vehicle Details > Invoicing tab, PAPS # the vehicle is associated with.

**Buyer**

Vehicle Details > Exporter tab, Buyer Company field.

**Destination**

Vehicle Details > Exporter tab, Vehicle Destination Company field.

**Parking Space**

Vehicle Details > Hero card.

**Mileage When Conformity Photos Taken**

Uses mileage in MI from one of these locations:

- If cluster conversion is **No**, use Vehicle Check-In tab > Vehicle Details card > Vehicle Check-In Odometer Reading in MI.
- If cluster conversion is **Yes** and Cluster Reinstallation Completed is blank on the Cluster Timeline, use Vehicle Check-In tab > Vehicle Details card > Vehicle Check-In Odometer Reading in MI.
- If cluster conversion is **Yes** and Cluster Reinstallation Completed is populated on the Cluster Timeline, use Vehicle Details > Cluster tab > Cluster Information card > Current Odometer Reading (After Cluster Reinstallation Complete).

**Exporter Notes**

Left blank for all records.

**Import Branch**

Inbound Trips/Inbound Trip Details, Import Branch field.

**Import Branch Name**

Left blank for all records.

**Importer Notes**

Left blank for all records.

**Invoiced**

Vehicle Details > Vehicle Status Checklist, date from the Invoiced step.

**Checked In**

Vehicle Details > Vehicle Status Checklist, date from the Vehicle Check-In Complete step.

**Release Date**

Vehicle Details > Vehicle Status Checklist, date from the Bond Released step.

**Cluster Pulled**

Vehicle Details > Cluster tab, date from the Cluster Pulled step of the Cluster Timeline.

**Ownership Received**

Vehicle Details > Documents, date the most recent CAN Ownership document was associated with the vehicle.

**Recall Complete**

Vehicle Details > Recall, Repaired Date/Time of the most recent Recall. If No Recall was recorded or no recall campaigns are associated with the vehicle, this field is blank.

**Sent to DOT**

Vehicle Details > Vehicle Status Checklist, date from the Submitted to DOT step.

**Signed Outbound BOL**

Vehicle Details > Vehicle Status Checklist, date from the Signed BOL Upload step.

**Bond Release Date**

Vehicle Details > Vehicle Status Checklist, date from the Bond Released step.`}
            </GreenText>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button color='inherit' disabled={loadingState.isDownloading} id='customer-export-report-cancel' onClick={onClose}>
          CANCEL
        </Button>

        <LoadingButton
          disabled={!selectedCompany?.companyGuid}
          id='customer-export-report-download'
          loading={loadingState.isDownloading}
          onClick={handleDownload}
          variant='contained'
        >
          DOWNLOAD
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
